// ASK-1 — permission-aware ask router (KB pilot artifacts, no step-through).

import type { AuthContextValue } from "@/contexts/AuthContext";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { getBucketForMode } from "@/components/shell/modes/bucketsRegistry";
import { bestIntentMatchScore } from "./matchAskQuery";
import type { FireflyAskArtifact, FireflyAskResolution } from "./tourTypes";

export type { FireflyAskResolution };

export const ASK_NOT_FOUND_MESSAGE =
  "I don't have an answer for that yet. Try rephrasing, or open Training mode for deeper KB search.";

export const ASK_MIN_MATCH_SCORE = 40;

export type CanAccessPrimaryModeFn = (
  mode: PrimaryMode,
  auth: AuthContextValue,
) => boolean;

export type ResolveFireflyAskInput = {
  queryText: string;
  artifacts: readonly FireflyAskArtifact[];
  auth: AuthContextValue;
  currentMode: PrimaryMode;
  canAccessPrimaryMode: CanAccessPrimaryModeFn;
};

function passesRoleGate(artifact: FireflyAskArtifact, auth: AuthContextValue): boolean {
  if (!artifact.requiredRoles?.length) return true;
  return auth.hasRole(...artifact.requiredRoles);
}

function passesBucketGate(
  artifact: FireflyAskArtifact,
  currentMode: PrimaryMode,
): boolean {
  const bucket = getBucketForMode(currentMode);
  if (!bucket) return true;
  return artifact.bucketRelevance.includes(bucket);
}

function accessDeniedMessage(mode: PrimaryMode): string {
  return `You don't have access to ${mode} in this workspace. Ask your admin if you need that surface.`;
}

export function resolveFireflyAsk(input: ResolveFireflyAskInput): FireflyAskResolution {
  const query = input.queryText.trim();
  if (!query) {
    return { kind: "not_found", message: ASK_NOT_FOUND_MESSAGE };
  }

  let best: { artifact: FireflyAskArtifact; score: number } | null = null;

  for (const artifact of input.artifacts) {
    if (!passesBucketGate(artifact, input.currentMode)) continue;
    if (!passesRoleGate(artifact, input.auth)) continue;
    const score = bestIntentMatchScore(query, artifact.intents);
    const modeBoost =
      artifact.navigateTo?.mode === input.currentMode ? 20 : 0;
    const total = score + modeBoost;
    if (total < ASK_MIN_MATCH_SCORE) continue;
    if (!best || total > best.score || (total === best.score && artifact.id < best.artifact.id)) {
      best = { artifact, score: total };
    }
  }

  if (!best) {
    return { kind: "not_found", message: ASK_NOT_FOUND_MESSAGE };
  }

  const { artifact } = best;
  const nav = artifact.navigateTo;

  if (nav && !input.canAccessPrimaryMode(nav.mode, input.auth)) {
    return {
      kind: "denied",
      artifactId: artifact.id,
      targetMode: nav.mode,
      message: accessDeniedMessage(nav.mode),
    };
  }

  return {
    kind: "answer",
    artifactId: artifact.id,
    answer: artifact.answer,
    navigateTo: nav,
    highlightTarget: artifact.highlightTarget,
    companionTourId: artifact.companionTourId,
  };
}

export function fireflyAskOutcome(
  resolution: FireflyAskResolution,
): "started" | "denied_permission" | "not_found" {
  if (resolution.kind === "answer") return "started";
  if (resolution.kind === "denied") return "denied_permission";
  return "not_found";
}
